import React from "react";
import styled from "styled-components";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const DashboardContainer = styled.div`
  margin-top: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  color: #333;
  margin: 30px 0 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const MetricCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #4285f4;
  margin-bottom: 5px;
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const ContentSection = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const ContentTitle = styled.h3`
  font-size: 16px;
  color: #333;
  margin-bottom: 10px;
`;

const ContentBox = styled.div`
  border: 1px solid #eee;
  padding: 15px;
  border-radius: 4px;
  background-color: #f9f9f9;
  white-space: pre-wrap;
  font-family: monospace;
  max-height: 300px;
  overflow-y: auto;
`;

const EmailPreview = styled.div`
  border: 1px solid #eee;
  padding: 15px;
  border-radius: 4px;
  background-color: white;
  max-height: 500px;
  overflow-y: auto;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
`;

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const ChartCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 300px;
`;

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

function SmsEmailDashboard({ reportData }) {
  if (!reportData || (!reportData.smsData && !reportData.emailData)) {
    return (
      <NoDataMessage>Aucune donnée disponible pour ce rapport</NoDataMessage>
    );
  }

  const { smsData, emailData, smsContent, emailHtml } = reportData;

  // Calculate SMS delivery rate
  const smsDeliveryRate = smsData
    ? (
        (parseInt(smsData["volume délivré"] || 0) /
          parseInt(smsData["volume cible"] || 1)) *
        100
      ).toFixed(1)
    : 0;

  // Calculate Email delivery rate
  const emailDeliveryRate = emailData
    ? (
        (parseInt(emailData["volume délivré"] || 0) /
          parseInt(emailData["volume cible"] || 1)) *
        100
      ).toFixed(1)
    : 0;

  // Calculate Email open rate
  const emailOpenRate = emailData
    ? (
        (parseInt(emailData["ouvertures"] || 0) /
          parseInt(emailData["volume délivré"] || 1)) *
        100
      ).toFixed(1)
    : 0;

  // Calculate Email click rate
  const emailClickRate = emailData
    ? (
        (parseInt(emailData["clics"] || 0) /
          parseInt(emailData["volume délivré"] || 1)) *
        100
      ).toFixed(1)
    : 0;

  // Prepare data for charts
  const prepareEmailFunnelData = () => {
    if (!emailData) return [];

    return [
      { name: "Volume cible", value: parseInt(emailData["volume cible"] || 0) },
      {
        name: "Volume délivré",
        value: parseInt(emailData["volume délivré"] || 0),
      },
      { name: "Ouvertures", value: parseInt(emailData["ouvertures"] || 0) },
      { name: "Clics", value: parseInt(emailData["clics"] || 0) },
    ];
  };

  const prepareEmailRatesData = () => {
    if (!emailData) return [];

    return [
      { name: "Taux de délivrance", value: parseFloat(emailDeliveryRate) },
      { name: "Taux d'ouverture", value: parseFloat(emailOpenRate) },
      { name: "Taux de clic", value: parseFloat(emailClickRate) },
    ];
  };

  const prepareSmsFunnelData = () => {
    if (!smsData) return [];

    return [
      { name: "Volume cible", value: parseInt(smsData["volume cible"] || 0) },
      {
        name: "Volume délivré",
        value: parseInt(smsData["volume délivré"] || 0),
      },
      {
        name: "Affichage preview",
        value: parseInt(smsData["affichage preview"] || 0),
      },
      {
        name: "Cliqueurs uniques",
        value: parseInt(smsData["cliqueurs uniques"] || 0),
      },
    ];
  };

  const emailFunnelData = prepareEmailFunnelData();
  const emailRatesData = prepareEmailRatesData();
  const smsFunnelData = prepareSmsFunnelData();

  return (
    <DashboardContainer>
      {smsData && (
        <>
          <SectionTitle>Rapport SMS</SectionTitle>
          <MetricsGrid>
            <MetricCard>
              <MetricValue>{smsData["volume cible"] || 0}</MetricValue>
              <MetricLabel>Volume cible</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{smsData["volume délivré"] || 0}</MetricValue>
              <MetricLabel>Volume délivré</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{smsDeliveryRate}%</MetricValue>
              <MetricLabel>Taux de délivrance</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{smsData["affichage preview"] || 0}</MetricValue>
              <MetricLabel>Affichage preview</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{smsData["cliqueurs uniques"] || 0}</MetricValue>
              <MetricLabel>Cliqueurs uniques</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{smsData["désabonnés"] || 0}</MetricValue>
              <MetricLabel>Désabonnés</MetricLabel>
            </MetricCard>
          </MetricsGrid>

          <ChartsContainer>
            <ChartCard>
              <ContentTitle>Entonnoir de conversion SMS</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={smsFunnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#4285f4" name="Nombre" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <ContentTitle>Taux de performance SMS</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={[
                      { name: "Délivrés", value: parseFloat(smsDeliveryRate) },
                      {
                        name: "Non délivrés",
                        value: 100 - parseFloat(smsDeliveryRate),
                      },
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(1)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[
                      { name: "Délivrés", value: parseFloat(smsDeliveryRate) },
                      {
                        name: "Non délivrés",
                        value: 100 - parseFloat(smsDeliveryRate),
                      },
                    ].map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartsContainer>

          {smsContent && (
            <ContentSection>
              <ContentTitle>Contenu du SMS</ContentTitle>
              <ContentBox>{smsContent}</ContentBox>
            </ContentSection>
          )}
        </>
      )}

      {emailData && (
        <>
          <SectionTitle>Rapport Email</SectionTitle>
          <MetricsGrid>
            <MetricCard>
              <MetricValue>{emailData["volume cible"] || 0}</MetricValue>
              <MetricLabel>Volume cible</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailData["volume délivré"] || 0}</MetricValue>
              <MetricLabel>Volume délivré</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailDeliveryRate}%</MetricValue>
              <MetricLabel>Taux de délivrance</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailData["ouvertures"] || 0}</MetricValue>
              <MetricLabel>Ouvertures</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailOpenRate}%</MetricValue>
              <MetricLabel>Taux d'ouverture</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailData["ouvreurs uniques"] || 0}</MetricValue>
              <MetricLabel>Ouvreurs uniques</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailData["clics"] || 0}</MetricValue>
              <MetricLabel>Clics</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailClickRate}%</MetricValue>
              <MetricLabel>Taux de clic</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailData["cliqueurs uniques"] || 0}</MetricValue>
              <MetricLabel>Cliqueurs uniques</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailData["désabonnés"] || 0}</MetricValue>
              <MetricLabel>Désabonnés</MetricLabel>
            </MetricCard>
          </MetricsGrid>

          <ChartsContainer>
            <ChartCard>
              <ContentTitle>Entonnoir de conversion Email</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={emailFunnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#4285f4" name="Nombre" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <ContentTitle>Taux de performance Email</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={emailRatesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    domain={[0, 100]}
                    label={{ value: "%", angle: -90, position: "insideLeft" }}
                  />
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Legend />
                  <Bar dataKey="value" fill="#00C49F" name="Taux (%)" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <ContentTitle>Comparaison des métriques</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: "Ouvertures",
                        value: parseInt(emailData["ouvertures"] || 0),
                      },
                      {
                        name: "Clics",
                        value: parseInt(emailData["clics"] || 0),
                      },
                      {
                        name: "Désabonnés",
                        value: parseInt(emailData["désabonnés"] || 0),
                      },
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(1)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[
                      {
                        name: "Ouvertures",
                        value: parseInt(emailData["ouvertures"] || 0),
                      },
                      {
                        name: "Clics",
                        value: parseInt(emailData["clics"] || 0),
                      },
                      {
                        name: "Désabonnés",
                        value: parseInt(emailData["désabonnés"] || 0),
                      },
                    ].map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartsContainer>

          {emailHtml && (
            <ContentSection>
              <ContentTitle>Aperçu de l'Email</ContentTitle>
              <EmailPreview dangerouslySetInnerHTML={{ __html: emailHtml }} />
            </ContentSection>
          )}
        </>
      )}

      {emailData && smsData && (
        <>
          <SectionTitle>Comparaison Email vs SMS</SectionTitle>
          <ChartsContainer>
            <ChartCard>
              <ContentTitle>Taux de délivrance</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={[
                    { name: "Email", value: parseFloat(emailDeliveryRate) },
                    { name: "SMS", value: parseFloat(smsDeliveryRate) },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    domain={[0, 100]}
                    label={{ value: "%", angle: -90, position: "insideLeft" }}
                  />
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Legend />
                  <Bar
                    dataKey="value"
                    name="Taux de délivrance (%)"
                    fill="#8884d8"
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <ContentTitle>Volumes</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart
                  data={[
                    {
                      name: "Cible",
                      email: parseInt(emailData["volume cible"] || 0),
                      sms: parseInt(smsData["volume cible"] || 0),
                    },
                    {
                      name: "Délivré",
                      email: parseInt(emailData["volume délivré"] || 0),
                      sms: parseInt(smsData["volume délivré"] || 0),
                    },
                    {
                      name: "Interactions",
                      email: parseInt(emailData["ouvertures"] || 0),
                      sms: parseInt(smsData["affichage preview"] || 0),
                    },
                    {
                      name: "Clics",
                      email: parseInt(emailData["clics"] || 0),
                      sms: parseInt(smsData["cliqueurs uniques"] || 0),
                    },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="email"
                    stroke="#8884d8"
                    name="Email"
                    activeDot={{ r: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="sms"
                    stroke="#82ca9d"
                    name="SMS"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartsContainer>
        </>
      )}
    </DashboardContainer>
  );
}

export default SmsEmailDashboard;
